import type { AccessRightsModuleDto } from '@app/services/rtkQuery/types';
import type { UserAccessRightsInterface } from '@app/types/NewAccessRight';

export const accessRightsInitialState: {
  user: UserAccessRightsInterface;
  organization: AccessRightsModuleDto[];
  isLoading: boolean;
} = {
  user: {
    UserId: 0,
    IsAdmin: false,
    IsSuperAdmin: false,
    Analytics: {
      IsOn: false,
      FinancialIncomesReport: false,
      FinancialFactImplReport: false,
      FinancialExpenseByArticlesReport: false,
      FinancialPnlReport: false,
      FinancialDetailsOfExpensesReport: false,
      FinancialBranchIndicatorsReport: false,
    },
    Salaries: {
      IsOn: false,
      EditBeforeConfirm: false,
      EditAfterConfirm: false,
      Confirm: false,
      EditEmployeePayoff: false,
    },
    PaymentAccounts: {
      IsOn: false,
      IsCreate: false,
      IsEdit: false,
      IsDelete: false,
    },
    Chains: {
      IsOn: false,
      IsCreate: false,
      IsEdit: false,
    },
    Branches: {
      IsOn: false,
      IsCreate: false,
      IsEdit: false,
    },
    Clients: {
      IsOn: false,
      IsCreate: false,
      IsEdit: false,
      IsDelete: false,
      IsTagsChange: false,
      IsExport: false,
      IsCommunicate: false,
      IsViewCommunication: false,
      IsShowContacts: false,
    },
    Users: {
      IsOn: false,
      IsCreate: false,
      IsEdit: false,
      IsDelete: false,
      IsExport: false,
      IsImport: false,
      IsChangeOwner: false,
      IsChangeBranches: false,
      IsUsers: false,
      IsEmployees: false,
    },
    Records: {
      IsOn: false,
      IsCreate: false,
      IsEdit: false,
      IsDelete: false,
      IsExport: false,
      IsJournal: false,
      IsRegister: false,
      IsStatuses: false,
      IsViewAllRecords: false,
      IsPayRecord: false,
      IsFinancialStatistics: false,
      IsViewReminders: false,
      IsEditReminders: false,
      IsEditPaidRecord: false,
      IsViewPastRecords: false,
    },
    LegalEntities: {
      IsOn: false,
      IsCreate: false,
      IsEdit: false,
      IsDelete: false,
    },
    Integrations: {
      IsOn: false,
      IsCreate: false,
      IsEdit: false,
      IsDelete: false,
    },
    SalePlans: {
      IsOn: false,
      IsCreate: false,
      IsExport: false,
    },
    SalesRegister: {
      IsOn: false,
      IsDeleteSale: false,
      IsCreateExpense: false,
      IsCreateCollection: false,
      IsToppingUpCash: false,
      IsDeleteExpense: false,
      IsDeleteCollection: false,
      IsTopClientBalance: false,
      IsReturnMoney: false,
      IsExport: false,
    },
    AccessRights: {
      IsOn: false,
      IsCreateTemplates: false,
      IsDeleteTemplates: false,
      IsEdit: false,
      EditingRightsWithinOpenRights: false,
    },
    ClientServices: {
      IsOn: false,
      IsClientServices: false,
      IsServiceCategories: false,
      IsCreate: false,
      IsEdit: false,
      IsDelete: false,
      IsExport: false,
      IsImport: false,
      IsCategoryCreate: false,
      IsCategoryEdit: false,
      IsCategoryDelete: false,
      IsAttach: false,
    },
    Positions: {
      IsOn: false,
      IsCreate: false,
      IsEdit: false,
      IsDelete: false,
    },
    DataMigrations: {
      IsOn: false,
      IsCreate: false,
    },
    Leads: {
      IsOn: false,
      IsDelete: false,
      IsOwnerChange: false,
      IsViewAll: false,
      IsAllowSpam: false,
      IsTake: false,
      IsSaleFunnelChange: false,
      IsCommunicate: false,
    },
    WorkSchedules: {
      IsOn: false,
      IsScheduleView: false,
      IsCreate: false,
      IsEdit: false,
      IsDelete: false,
      IsEditPast: false,
      IsDeletePast: false,
      IsCreateBreak: false,
      IsEditBreak: false,
      IsDeleteBreak: false,
    },
    SalesFunnels: {
      IsOn: false,
      IsFunnelCreate: false,
      IsFunnelEdit: false,
      IsFunnelDelete: false,
      IsStageCreate: false,
      IsStageEdit: false,
      IsStageDelete: false,
    },
    History: { IsOn: false },
    CrmTasks: {
      IsOn: false,
      IsViewAll: false,
      IsCreate: false,
      IsAssign: false,
      IsEdit: false,
      IsEditAll: false,
      IsChangeStatus: false,
      IsDelete: false,
    },
    Sendouts: {
      IsOn: false,
      IsCreate: false,
      IsControl: false,
      IsViewReceivers: false,
      IsEdit: false,
    },
    AiOperator: { IsOn: false },
    Organizations: {
      IsOn: false,
      SecretLoginToOrganizationAccount: false,
    },
    MessageTemplates: { IsOn: false },
    Modules: [],
    Warehouse: {
      IsInventoryComplete: false,
      IsInventoryCompleting: false,
      IsInventoryCreate: false,
      IsInventoryDelete: false,
      IsInventoryEdit: false,
      IsInventoryReports: false,
      IsOn: false,
      IsProductItemsAccept: false,
      IsProductItemsDecomission: false,
      IsProductItemsDelete: false,
      IsProductItemsMove: false,
      IsSupplierCreate: false,
      IsSupplierDelete: false,
      IsSupplierEdit: false,
      IsSuppliers: false,
      IsWarehouseCreate: false,
      IsWarehouseDelete: false,
      IsWarehouseEdit: false,
      IsViewAllWarehouses: false,
      IsExportWarehouseProductsToExcel: false,
    },
    Products: {
      IsBrandCreate: false,
      IsBrandDelete: false,
      IsBrandEdit: false,
      IsBrands: false,
      IsOn: false,
      IsProductCategories: false,
      IsProductCategoryCreate: false,
      IsProductCategoryDelete: false,
      IsProductCategoryEdit: false,
      IsProductCreate: false,
      IsProductDelete: false,
      IsProductEdit: false,
    },
    Tags: { IsOn: false },
    // Бэк пока не отдаёт блок MedicalDocuments — до его выката действует
    // безопасный дефолт «разрешено» (текущее поведение: медкарта открыта).
    // TODO(egisz-stage4, B18): после выката блока на бэке перевести дефолты
    // в false — источником станет ответ GetUserAccessRights.
    MedicalDocuments: {
      IsOn: true,
      IsEdit: true,
      IsSign: true,
      IsSend: true,
      IsViewAll: true,
    },
    // Лояльность и Онлайн запись (us/11069): по умолчанию скрыты, источником
    // истины является ответ GetUserAccessRights.
    Loyalty: { IsOn: false },
    OnlineRecording: { IsOn: false, IsEdit: false },
  },
  organization: [],
  isLoading: false,
};

export const adminAccessRights: UserAccessRightsInterface = {
  UserId: 0,
  IsAdmin: true,
  IsSuperAdmin: true,
  Analytics: {
    IsOn: true,
    FinancialIncomesReport: true,
    FinancialFactImplReport: true,
    FinancialExpenseByArticlesReport: true,
    FinancialPnlReport: true,
    FinancialDetailsOfExpensesReport: true,
    FinancialBranchIndicatorsReport: true,
  },
  Salaries: {
    IsOn: true,
    EditBeforeConfirm: true,
    EditAfterConfirm: true,
    Confirm: true,
    EditEmployeePayoff: true,
  },
  PaymentAccounts: {
    IsOn: true,
    IsCreate: true,
    IsEdit: true,
    IsDelete: true,
  },
  Chains: {
    IsOn: true,
    IsCreate: true,
    IsEdit: true,
  },
  Branches: {
    IsOn: true,
    IsCreate: true,
    IsEdit: true,
  },
  Clients: {
    IsOn: true,
    IsCreate: true,
    IsEdit: true,
    IsDelete: true,
    IsTagsChange: true,
    IsExport: true,
    IsCommunicate: true,
    IsViewCommunication: true,
    IsShowContacts: true,
  },
  Users: {
    IsOn: true,
    IsCreate: true,
    IsEdit: true,
    IsDelete: true,
    IsExport: true,
    IsImport: true,
    IsChangeOwner: true,
    IsChangeBranches: true,
    IsUsers: true,
    IsEmployees: true,
  },
  Records: {
    IsOn: true,
    IsCreate: true,
    IsEdit: true,
    IsDelete: true,
    IsExport: true,
    IsJournal: true,
    IsRegister: true,
    IsStatuses: true,
    IsViewAllRecords: true,
    IsPayRecord: true,
    IsFinancialStatistics: true,
    IsViewReminders: true,
    IsEditReminders: true,
    IsEditPaidRecord: true,
    IsViewPastRecords: true,
  },
  LegalEntities: {
    IsOn: true,
    IsCreate: true,
    IsEdit: true,
    IsDelete: true,
  },
  Integrations: {
    IsOn: true,
    IsCreate: true,
    IsEdit: true,
    IsDelete: true,
  },
  SalePlans: {
    IsOn: true,
    IsCreate: true,
    IsExport: true,
  },
  SalesRegister: {
    IsOn: true,
    IsDeleteSale: true,
    IsCreateExpense: true,
    IsCreateCollection: true,
    IsToppingUpCash: true,
    IsDeleteExpense: true,
    IsDeleteCollection: true,
    IsTopClientBalance: true,
    IsReturnMoney: true,
    IsExport: true,
  },
  AccessRights: {
    IsOn: true,
    IsCreateTemplates: true,
    IsDeleteTemplates: true,
    IsEdit: true,
    EditingRightsWithinOpenRights: true,
  },
  ClientServices: {
    IsOn: true,
    IsClientServices: true,
    IsServiceCategories: true,
    IsCreate: true,
    IsEdit: true,
    IsDelete: true,
    IsExport: true,
    IsImport: true,
    IsCategoryCreate: true,
    IsCategoryEdit: true,
    IsCategoryDelete: true,
    IsAttach: true,
  },
  Positions: {
    IsOn: true,
    IsCreate: true,
    IsEdit: true,
    IsDelete: true,
  },
  DataMigrations: {
    IsOn: true,
    IsCreate: true,
  },
  Leads: {
    IsOn: true,
    IsDelete: true,
    IsOwnerChange: true,
    IsViewAll: true,
    IsAllowSpam: true,
    IsTake: true,
    IsSaleFunnelChange: true,
    IsCommunicate: true,
  },
  WorkSchedules: {
    IsOn: true,
    IsScheduleView: true,
    IsCreate: true,
    IsEdit: true,
    IsDelete: true,
    IsEditPast: true,
    IsDeletePast: true,
    IsCreateBreak: true,
    IsEditBreak: true,
    IsDeleteBreak: true,
  },
  SalesFunnels: {
    IsOn: true,
    IsFunnelCreate: true,
    IsFunnelEdit: true,
    IsFunnelDelete: true,
    IsStageCreate: true,
    IsStageEdit: true,
    IsStageDelete: true,
  },
  History: { IsOn: true },
  CrmTasks: {
    IsOn: true,
    IsViewAll: true,
    IsCreate: true,
    IsAssign: true,
    IsEdit: true,
    IsEditAll: true,
    IsChangeStatus: true,
    IsDelete: true,
  },
  Sendouts: {
    IsOn: true,
    IsCreate: true,
    IsControl: true,
    IsViewReceivers: true,
    IsEdit: true,
  },
  AiOperator: { IsOn: true },
  Organizations: {
    IsOn: true,
    SecretLoginToOrganizationAccount: true,
  },
  MessageTemplates: { IsOn: true },
  Modules: [],
  Warehouse: {
    IsInventoryComplete: true,
    IsInventoryCompleting: true,
    IsInventoryCreate: true,
    IsInventoryDelete: true,
    IsInventoryEdit: true,
    IsInventoryReports: true,
    IsOn: true,
    IsProductItemsAccept: true,
    IsProductItemsDecomission: true,
    IsProductItemsDelete: true,
    IsProductItemsMove: true,
    IsSupplierCreate: true,
    IsSupplierDelete: true,
    IsSupplierEdit: true,
    IsSuppliers: true,
    IsWarehouseCreate: true,
    IsWarehouseDelete: true,
    IsWarehouseEdit: true,
    IsViewAllWarehouses: true,
    IsExportWarehouseProductsToExcel: true,
  },
  Products: {
    IsBrandCreate: true,
    IsBrandDelete: true,
    IsBrandEdit: true,
    IsBrands: true,
    IsOn: true,
    IsProductCategories: true,
    IsProductCategoryCreate: true,
    IsProductCategoryDelete: true,
    IsProductCategoryEdit: true,
    IsProductCreate: true,
    IsProductDelete: true,
    IsProductEdit: true,
  },
  Tags: { IsOn: true },
  MedicalDocuments: {
    IsOn: true,
    IsEdit: true,
    IsSign: true,
    IsSend: true,
    IsViewAll: true,
  },
  Loyalty: { IsOn: true },
  OnlineRecording: { IsOn: true, IsEdit: true },
};
